// Extract a Vimeo numeric video ID from common URL forms, or accept a bare ID.
//   https://vimeo.com/123456789
//   https://vimeo.com/123456789/abcd1234            (private link with hash)
//   https://player.vimeo.com/video/123456789
//   123456789
//
// Vimeo IDs are digits only (typically 8–12 digits in 2026).

export function extractVimeoId(input: string | null | undefined): {
  id: string;
  hash?: string;
} | null {
  if (!input) return null;
  const value = input.trim();
  if (!value) return null;

  // Bare numeric ID.
  if (/^\d{6,}$/.test(value)) return { id: value };

  try {
    const url = new URL(value);
    const host = url.hostname.replace(/^www\./, "");
    if (host !== "vimeo.com" && host !== "player.vimeo.com") return null;

    const parts = url.pathname.split("/").filter(Boolean);
    // player.vimeo.com/video/<id>(/<hash>)
    if (host === "player.vimeo.com" && parts[0] === "video" && /^\d+$/.test(parts[1] || "")) {
      return { id: parts[1], hash: parts[2] || undefined };
    }
    // vimeo.com/<id>(/<hash>)
    if (host === "vimeo.com" && /^\d+$/.test(parts[0] || "")) {
      return { id: parts[0], hash: parts[1] || undefined };
    }
  } catch {
    // Not a URL.
  }
  return null;
}

export function vimeoEmbedUrl(id: string, hash?: string): string {
  const base = `https://player.vimeo.com/video/${id}`;
  const qs = new URLSearchParams({
    title: "0",
    byline: "0",
    portrait: "0",
    dnt: "1",
  });
  if (hash) qs.set("h", hash);
  return `${base}?${qs.toString()}`;
}

export function vimeoWatchUrl(id: string, hash?: string): string {
  // Append `fl=ml&fe=ec` — Vimeo's private "managed link" tokens that grant
  // password-free access to unlisted/protected videos when opened directly.
  const base = hash ? `https://vimeo.com/${id}/${hash}` : `https://vimeo.com/${id}`;
  return `${base}?fl=ml&fe=ec`;
}

// ── Thumbnails ───────────────────────────────────────────────────────────────
//
// YouTube serves a still image at an address you can compute from the id.
// Vimeo does not: the image lives under an opaque path, and the only public
// way to learn it is the oEmbed endpoint, which answers with JSON that
// includes `thumbnail_url`. That image is the picture Vimeo itself shows for
// the video, so changing which frame it is happens in Vimeo's video settings
// (Thumbnail), not here.

/** Where the oEmbed answer for a video lives. Never built from free text. */
export function vimeoOembedUrl(id: string, hash?: string, width = 960): string {
  // Unlisted videos only answer when asked about their private link.
  const video = hash ? `https://vimeo.com/${id}/${hash}` : `https://vimeo.com/${id}`;
  const qs = new URLSearchParams({ url: video, width: String(width), dnt: "1" });
  return `https://vimeo.com/api/oembed.json?${qs.toString()}`;
}

/**
 * The thumbnail out of an oEmbed answer, or null if it is not one we trust.
 *
 * The result ends up in an <img src>, so it has to be an https image on
 * Vimeo's own CDN. Anything else in that field (a misbehaving proxy, a
 * changed API) is dropped instead of rendered.
 */
export function parseVimeoThumbnail(body: unknown): string | null {
  if (!body || typeof body !== "object") return null;
  const raw = (body as { thumbnail_url?: unknown }).thumbnail_url;
  if (typeof raw !== "string") return null;
  try {
    const url = new URL(raw);
    if (url.protocol !== "https:") return null;
    if (!/(^|\.)vimeocdn\.com$/i.test(url.hostname)) return null;
    return url.toString();
  } catch {
    return null;
  }
}

/**
 * Ask Vimeo for a video's thumbnail.
 *
 * Resolves to null when Vimeo answered and there is nothing to show (private
 * or removed video, embedding restricted, unexpected body): asking again would
 * not help. It throws when the request itself failed (offline, blocked, 5xx),
 * so the caller can retry and, importantly, so a blip is not remembered as
 * "this video has no thumbnail" for the rest of the visit.
 */
export async function fetchVimeoThumbnail(
  oembedUrl: string,
  signal?: AbortSignal,
): Promise<string | null> {
  const res = await fetch(oembedUrl, {
    signal,
    // No cookies to vimeo.com: this is a public lookup.
    credentials: "omit",
    headers: { Accept: "application/json" },
  });
  if (res.status >= 500) throw new Error(`Vimeo oEmbed answered ${res.status}`);
  if (!res.ok) return null;
  try {
    return parseVimeoThumbnail(await res.json());
  } catch {
    return null;
  }
}
